import Phaser from "phaser";
import { getCardArtUrl } from "../../assets/cardArt.js";
import { getStoryBattlefieldUrl } from "../../assets/storyArt.js";
import {
  objectValue,
  type BoardPosition,
  type Depth,
  type DreamObjectState,
  type Lane,
  type PlayerIndex,
  type ResolutionEvent,
  type ResolutionFrame,
} from "../../../shared/core/index.js";
import {
  type BattleSession,
  type BattleUiState,
} from "../../app/BattleSession.js";
import type { SeCueId } from "../../audio/AudioCue.js";
import { ChapterBattleSession } from "../../app/ChapterBattleSession.js";
import { AnimationDirector, type BattleAnimationPort } from "../animation/AnimationDirector.js";
import {
  GAME_EVENTS,
  SCENE_KEYS,
} from "../constants.js";

interface DreamObjectView {
  container: Phaser.GameObjects.Container;
  card: Phaser.GameObjects.Graphics;
  art: Phaser.GameObjects.Image | null;
  shade: Phaser.GameObjects.Graphics;
  fallbackSymbol: Phaser.GameObjects.Text;
  tag: Phaser.GameObjects.Text;
  valueBadge: Phaser.GameObjects.Graphics;
  title: Phaser.GameObjects.Text;
  value: Phaser.GameObjects.Text;
}

const LANES = [1, 2, 3] as const;
const DEPTHS = [3, 2, 1] as const;
const STORY_FIELD_TEXTURE = "story-field";
const CARD_ART_TEXTURE_PREFIX = "card-art:";
const OBJECT_CARD = {
  width: 176,
  height: 108,
  artWidth: 168,
  artHeight: 100,
};
const BOARD = {
  x: 72,
  y: 72,
  width: 816,
  height: 390,
  cellWidth: 272,
  cellHeight: 130,
};

export class BattleScene extends Phaser.Scene implements BattleAnimationPort {
  private readonly objectViews = new Map<string, DreamObjectView>();
  private readonly laneHeaders = new Map<Lane, Phaser.GameObjects.Text>();
  private director?: AnimationDirector;
  private selectionGraphics?: Phaser.GameObjects.Graphics;
  private previousFocusMarkers?: Phaser.GameObjects.Container;
  private unsubscribeSession?: () => void;

  public constructor(private readonly session: BattleSession = new ChapterBattleSession()) {
    super(SCENE_KEYS.battle);
  }

  public preload(): void {
    for (const cardId of this.collectPotentialObjectArtIds()) {
      const artUrl = getCardArtUrl(cardId);
      const textureKey = this.cardArtTextureKey(cardId);
      if (artUrl && !this.textures.exists(textureKey)) this.load.image(textureKey, artUrl);
    }
    if (this.session.mode === "story") {
      const fieldUrl = getStoryBattlefieldUrl(this.session.scenarioId);
      if (fieldUrl) this.load.image(STORY_FIELD_TEXTURE, fieldUrl);
    }
  }

  public create(): void {
    this.input.setTopOnly(true);
    this.cameras.main.setBackgroundColor("#0b1d25");
    this.drawBoard();
    this.selectionGraphics = this.add.graphics().setDepth(2);

    this.director = new AnimationDirector(this);
    this.unsubscribeSession = this.session.subscribe?.(() => {
      if (this.director?.isPlaying) return;
      this.renderState();
      this.emitHappiness(0, this.session.state.players[0].happiness);
      this.emitHappiness(1, this.session.state.players[1].happiness);
      this.emitUiState();
    });
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubscribeSession?.();
      this.session.dispose?.();
    });
    this.renderState();
    this.emitHappiness(0, this.session.state.players[0].happiness);
    this.emitHappiness(1, this.session.state.players[1].happiness);
    this.emitUiState();
    this.announce(this.session.startLog);
    this.game.events.emit(GAME_EVENTS.battleReady);
  }

  public setAnimationSpeed(speed: number): void {
    this.tweens.timeScale = Phaser.Math.Clamp(speed, 0.25, 4);
    this.time.timeScale = Phaser.Math.Clamp(speed, 0.25, 4);
  }

  public selectCard(instanceId: string): void {
    this.session.selectCard(instanceId);
    this.refreshPlanningDisplay();
  }

  public selectDevotion(): void {
    this.session.selectDevotion();
    this.refreshPlanningDisplay();
  }

  public selectDevotionTarget(playerIndex: PlayerIndex): void {
    this.session.selectDevotionTarget(playerIndex);
    this.refreshPlanningDisplay();
  }

  public selectFocus(lane: Lane): void {
    this.session.selectFocus(lane);
    this.refreshPlanningDisplay();
  }

  public async submitAction(): Promise<void> {
    await this.submit(false);
  }

  public async submitPass(): Promise<void> {
    await this.submit(true);
  }

  public async resetChapter(): Promise<void> {
    if (this.director?.isPlaying) return;
    this.tweens.killAll();
    await this.session.reset();
    this.game.events.emit(GAME_EVENTS.logReset);
    this.renderState();
    this.emitHappiness(0, this.session.state.players[0].happiness);
    this.emitHappiness(1, this.session.state.players[1].happiness);
    this.emitUiState();
    this.announce(this.session.startLog);
  }

  public announce(message: string): void {
    this.game.events.emit(GAME_EVENTS.status, message);
  }

  public presentResolutionEvent(event: ResolutionEvent, index: number, total: number): void {
    this.game.events.emit(GAME_EVENTS.resolutionEvent, { event, index, total });
  }

  public playAudioCue(cue: SeCueId): void {
    this.game.events.emit(GAME_EVENTS.audioCue, cue);
  }

  public animateMove(event: ResolutionEvent): Promise<void> {
    if (!event.objectId || !event.to) return Promise.resolve();
    const view = this.objectViews.get(event.objectId);
    if (!view) return Promise.resolve();
    const target = this.positionToPoint(event.to);

    return new Promise((resolve) => {
      this.tweens.add({
        targets: view.container,
        x: target.x,
        y: target.y,
        duration: 720,
        ease: "Sine.InOut",
        onComplete: () => resolve(),
      });
    });
  }

  public animateMerge(event: ResolutionEvent): Promise<void> {
    const removedObjectIds = event.removedObjectIds ?? [];
    const result = event.objectId
      ? event.frame.board.find((object) => object.id === event.objectId)
      : undefined;
    if (!result) return Promise.resolve();

    const sources = removedObjectIds
      .map((id) => this.objectViews.get(id))
      .filter((view): view is DreamObjectView => Boolean(view));
    const target = this.positionToPoint(result);

    return new Promise((resolve) => {
      if (sources.length === 0) {
        this.showMergedResult(result, resolve);
        return;
      }
      this.tweens.add({
        targets: sources.map((source) => source.container),
        x: target.x,
        y: target.y,
        duration: 520,
        ease: "Sine.InOut",
        onComplete: () => {
          this.tweens.add({
            targets: sources.map((source) => source.container),
            alpha: 0,
            scaleX: 1.16,
            scaleY: 1.16,
            duration: 300,
            ease: "Quad.In",
            onComplete: () => {
              for (const id of removedObjectIds) this.removeObjectView(id);
              this.showMergedResult(result, resolve);
            },
          });
        },
      });
    });
  }

  public animateAppear(event: ResolutionEvent): Promise<void> {
    if (!event.objectId) return Promise.resolve();
    const object = event.frame.board.find((item) => item.id === event.objectId);
    if (!object) return Promise.resolve();
    const view = this.objectViews.get(object.id) ?? this.createObjectView(object, 0.72, 0);

    return new Promise((resolve) => {
      this.tweens.add({
        targets: view.container,
        alpha: 1,
        scaleX: 1,
        scaleY: 1,
        duration: 480,
        ease: "Back.Out",
        onComplete: () => resolve(),
      });
    });
  }

  public animateExit(event: ResolutionEvent): Promise<void> {
    if (!event.objectId) return Promise.resolve();
    const view = this.objectViews.get(event.objectId);
    if (!view) return Promise.resolve();

    return new Promise((resolve) => {
      this.tweens.add({
        targets: view.container,
        alpha: 0,
        scaleX: 0.82,
        scaleY: 0.82,
        duration: 460,
        ease: "Sine.In",
        onComplete: () => {
          this.removeObjectView(event.objectId ?? "");
          resolve();
        },
      });
    });
  }

  public animateHappiness(event: ResolutionEvent): Promise<void> {
    if (event.before === undefined || event.after === undefined) return Promise.resolve();
    const difference = event.after - event.before;
    const changes = (event.impactBreakdown?.length
      ? event.impactBreakdown
      : [difference]).filter((value) => value !== 0);
    const playerIndex = event.playerIndex;

    if (playerIndex === undefined) return Promise.resolve();
    if (changes.length === 0) {
      this.emitHappiness(playerIndex, event.after);
      return Promise.resolve();
    }

    this.game.events.emit(GAME_EVENTS.happinessDelta, {
      playerIndex: this.displayPlayerIndex(playerIndex),
      changes,
      speed: this.tweens.timeScale,
    });
    const stagger = Math.max(0, changes.length - 1) * 90;

    return new Promise((resolve) => {
      this.time.delayedCall(380 + stagger, () => {
        this.emitHappiness(playerIndex, event.after ?? event.before ?? 0);
      });
      this.time.delayedCall(980 + stagger, () => resolve());
    });
  }

  public syncFrame(frame: ResolutionFrame): void {
    const currentIds = new Set(frame.board.map((object) => object.id));
    for (const id of [...this.objectViews.keys()]) {
      if (!currentIds.has(id)) this.removeObjectView(id);
    }
    for (const object of frame.board) {
      const view = this.objectViews.get(object.id);
      if (!view) {
        this.createObjectView(object);
        continue;
      }
      const point = this.positionToPoint(object);
      view.container.setPosition(point.x, point.y).setAlpha(1).setScale(1);
      this.updateObjectView(view, object, false);
    }
    this.updateLaneHeaders(frame.board);
  }

  private async submit(isPass: boolean): Promise<void> {
    if (!this.director || this.director.isPlaying) return;
    const resolution = await this.session.prepareResolution(isPass);
    if (!resolution) {
      this.refreshPlanningDisplay();
      return;
    }

    this.game.events.emit(GAME_EVENTS.resolutionStart, resolution);
    this.emitUiState();
    this.clearSelectionGraphics();
    await this.director.play(resolution.events);
    this.session.finishResolution();
    this.renderState();
    this.emitUiState();
    this.game.events.emit(GAME_EVENTS.resolutionEnd, resolution);

    if (this.session.state.result) {
      this.announce("夜明けです。" + this.session.state.result.title + "。");
    } else {
      this.playAudioCue("time_chime");
      this.announce("第" + this.session.state.turn + "時間の計画を立てます。");
    }
  }

  private drawBoard(): void {
    if (this.session.mode === "story") {
      if (this.textures.exists(STORY_FIELD_TEXTURE)) {
        this.add.image(
          BOARD.x + BOARD.width / 2,
          BOARD.y + BOARD.height / 2,
          STORY_FIELD_TEXTURE,
        )
          .setDisplaySize(BOARD.width, BOARD.height)
          .setAlpha(0.82)
          .setDepth(-2);
      }
    }

    const background = this.add.graphics();
    background.fillStyle(
      this.session.mode === "story" ? 0x071820 : 0x102c35,
      this.session.mode === "story" ? 0.42 : 1,
    );
    background.fillRoundedRect(BOARD.x, BOARD.y, BOARD.width, BOARD.height, 8);
    background.lineStyle(2, 0x9d8550, 0.75);
    background.strokeRoundedRect(BOARD.x, BOARD.y, BOARD.width, BOARD.height, 8);
    background.lineStyle(1, 0x806d48, 0.58);

    for (let column = 1; column < 3; column += 1) {
      const x = BOARD.x + BOARD.cellWidth * column;
      background.lineBetween(x, BOARD.y, x, BOARD.y + BOARD.height);
    }
    for (let row = 1; row < 3; row += 1) {
      const y = BOARD.y + BOARD.cellHeight * row;
      background.lineBetween(BOARD.x, y, BOARD.x + BOARD.width, y);
    }

    for (const lane of LANES) {
      const header = this.add.text(
        BOARD.x + BOARD.cellWidth * (lane - 0.5),
        BOARD.y - 27,
        "レーン " + lane,
        {
          color: "#b9a978",
          fontFamily: "sans-serif",
          fontSize: "14px",
          fontStyle: "bold",
        },
      ).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(5);
      header.on("pointerdown", () => {
        this.playAudioCue("ui_select");
        this.session.selectFocus(lane);
        this.refreshPlanningDisplay();
      });
      this.laneHeaders.set(lane, header);
    }
    this.previousFocusMarkers = this.add.container(0, 0).setDepth(7);

    for (const depth of DEPTHS) {
      const row = 3 - depth;
      this.add.text(
        BOARD.x - 38,
        BOARD.y + BOARD.cellHeight * (row + 0.5),
        String(depth),
        { color: "#829c91", fontFamily: "sans-serif", fontSize: "14px" },
      ).setOrigin(0.5);

      for (const lane of LANES) {
        const point = this.positionToPoint({ lane, depth });
        const zone = this.add.zone(point.x, point.y, BOARD.cellWidth, BOARD.cellHeight)
          .setInteractive({ useHandCursor: true })
          .setDepth(1);
        zone.on("pointerdown", () => {
          this.playAudioCue("ui_select");
          this.session.selectBoardPosition(lane, depth);
          this.refreshPlanningDisplay();
        });
      }
    }

    this.add.text(34, BOARD.y - 27, "深度", {
      color: "#829c91",
      fontFamily: "sans-serif",
      fontSize: "12px",
    }).setOrigin(0.5);
  }

  private renderState(): void {
    for (const view of this.objectViews.values()) view.container.destroy(true);
    this.objectViews.clear();
    for (const object of this.session.state.board) this.createObjectView(object);
    this.refreshPlanningDisplay(false);
  }

  private createObjectView(object: DreamObjectState, scale = 1, alpha = 1): DreamObjectView {
    const point = this.positionToPoint(object);
    const container = this.add.container(point.x, point.y)
      .setScale(scale)
      .setAlpha(alpha)
      .setDepth(10)
      .setSize(OBJECT_CARD.width + 8, OBJECT_CARD.height + 4)
      .setInteractive({ useHandCursor: true });
    const card = this.add.graphics();
    const art = this.createObjectArt(object.kind);
    const shade = this.add.graphics();
    const fallbackSymbol = this.add.text(0, -8, "◇", {
      color: "#b9d4d0",
      fontFamily: "serif",
      fontSize: "42px",
    }).setOrigin(0.5);
    const tag = this.add.text(-76, -43, "心象", {
      color: "#e8f3ee",
      fontFamily: "sans-serif",
      fontSize: "10px",
      fontStyle: "bold",
      backgroundColor: "#385057",
      padding: { x: 5, y: 2 },
    }).setOrigin(0, 0);
    const title = this.add.text(0, 34, object.name, {
      align: "center",
      color: "#f3ead6",
      fontFamily: "serif",
      fontSize: "14px",
      fontStyle: "bold",
      stroke: "#071116",
      strokeThickness: 3,
      wordWrap: { width: 130 },
    }).setOrigin(0.5);
    const valueBadge = this.add.graphics();
    const value = this.add.text(62, -34, "", {
      color: "#fff5d5",
      fontFamily: "sans-serif",
      fontSize: "18px",
      fontStyle: "bold",
      stroke: "#071116",
      strokeThickness: 3,
    }).setOrigin(0.5);

    container.add([
      card,
      ...(art ? [art] : []),
      fallbackSymbol,
      shade,
      tag,
      title,
      valueBadge,
      value,
    ]);
    container.on("pointerdown", () => {
      this.playAudioCue("ui_select");
      this.session.selectBoardPosition(object.lane, object.depth, object.id);
      this.refreshPlanningDisplay();
    });
    const view = {
      container,
      card,
      art,
      shade,
      fallbackSymbol,
      tag,
      title,
      valueBadge,
      value,
    };
    this.objectViews.set(object.id, view);
    this.updateObjectView(view, object, this.isObjectSelected(object.id));
    return view;
  }

  private updateObjectView(
    view: DreamObjectView,
    object: DreamObjectState,
    selected: boolean,
  ): void {
    const currentValue = objectValue(object);
    const positive = currentValue >= 0;
    const opening = Boolean(object.isOpeningImpression);
    const marker = object.temporaryPotency > 0
      ? "火花"
      : object.framed > 0
        ? "額内"
        : object.fixedValue !== null && object.kind !== "merged"
          ? "標本"
          : object.guidedFor.some(Boolean)
            ? "針路"
            : opening ? "心象" : "";
    view.title
      .setText(object.name)
      .setFontSize(object.name.length > 9 ? 11 : object.name.length > 7 ? 12 : 14);
    view.value
      .setText((currentValue > 0 ? "+" : "") + currentValue)
      .setColor(positive ? "#fff0b4" : "#ffd7df");
    view.tag.setText(marker).setVisible(Boolean(marker));
    view.fallbackSymbol
      .setVisible(!view.art)
      .setColor(positive ? "#d8c27f" : "#c9a0ad");
    view.art
      ?.setAlpha(opening ? 0.82 : 1)
      .setTint(opening ? 0xcadbd7 : 0xffffff);
    view.card.clear();
    view.card.fillStyle(opening ? 0x455b61 : positive ? 0x315f4c : 0x633d4a, 1);
    view.card.fillRoundedRect(
      -OBJECT_CARD.width / 2,
      -OBJECT_CARD.height / 2,
      OBJECT_CARD.width,
      OBJECT_CARD.height,
      7,
    );
    view.card.lineStyle(
      selected ? 4 : opening ? 3 : 2,
      selected ? 0xf5d982 : opening ? 0xc8d9d7 : positive ? 0xd4bc78 : 0xc69aa2,
      1,
    );
    view.card.strokeRoundedRect(
      -OBJECT_CARD.width / 2,
      -OBJECT_CARD.height / 2,
      OBJECT_CARD.width,
      OBJECT_CARD.height,
      7,
    );
    view.shade.clear();
    if (opening) {
      view.shade.fillStyle(0x587075, 0.2);
      view.shade.fillRect(
        -OBJECT_CARD.artWidth / 2,
        -OBJECT_CARD.artHeight / 2,
        OBJECT_CARD.artWidth,
        OBJECT_CARD.artHeight,
      );
    }
    view.shade.fillStyle(0x061116, 0.82);
    view.shade.fillRect(-OBJECT_CARD.artWidth / 2, 17, OBJECT_CARD.artWidth, 33);
    view.valueBadge.clear();
    view.valueBadge.fillStyle(positive ? 0x536f49 : 0x744550, 0.96);
    view.valueBadge.fillCircle(62, -34, 19);
    view.valueBadge.lineStyle(2, selected ? 0xffe49a : 0xd8c27f, 1);
    view.valueBadge.strokeCircle(62, -34, 19);
  }

  private createObjectArt(cardId: string): Phaser.GameObjects.Image | null {
    const textureKey = this.cardArtTextureKey(cardId);
    if (!this.textures.exists(textureKey)) return null;
    const art = this.add.image(0, 0, textureKey);
    const sourceWidth = art.width;
    const sourceHeight = art.height;
    const targetRatio = OBJECT_CARD.artWidth / OBJECT_CARD.artHeight;
    const sourceRatio = sourceWidth / sourceHeight;
    let cropX = 0;
    let cropY = 0;
    let cropWidth = sourceWidth;
    let cropHeight = sourceHeight;
    if (sourceRatio > targetRatio) {
      cropWidth = sourceHeight * targetRatio;
      cropX = (sourceWidth - cropWidth) / 2;
    } else {
      cropHeight = sourceWidth / targetRatio;
      cropY = (sourceHeight - cropHeight) / 2;
    }
    art
      .setCrop(cropX, cropY, cropWidth, cropHeight)
      .setDisplaySize(OBJECT_CARD.artWidth, OBJECT_CARD.artHeight);
    return art;
  }

  private collectPotentialObjectArtIds(): Set<string> {
    const cardIds = new Set<string>(["red_ball", "story_flying_dragon"]);
    for (const player of this.session.state.players) {
      for (const card of [...player.hand, ...player.deck, ...player.discard]) {
        cardIds.add(card.cardId);
      }
    }
    for (const object of this.session.state.board) cardIds.add(object.kind);
    for (const supplyCard of this.session.state.openSupply?.cards ?? []) {
      cardIds.add(supplyCard.cardId);
    }
    return cardIds;
  }

  private cardArtTextureKey(cardId: string): string {
    return CARD_ART_TEXTURE_PREFIX + cardId;
  }

  private refreshPlanningDisplay(emit = true): void {
    this.drawSelectionGraphics();
    this.updateLaneHeaders(this.session.state.board);
    this.updatePreviousFocusMarkers();
    for (const object of this.session.state.board) {
      const view = this.objectViews.get(object.id);
      if (view) this.updateObjectView(view, object, this.isObjectSelected(object.id));
    }
    if (emit) this.emitUiState();
  }

  private drawSelectionGraphics(): void {
    const graphics = this.selectionGraphics;
    if (!graphics) return;
    graphics.clear();
    if (this.session.phase !== "planning") return;

    const focusLane = this.session.draft.focusLane;
    graphics.fillStyle(0xe5c86f, 0.055);
    graphics.fillRect(
      BOARD.x + BOARD.cellWidth * (focusLane - 1),
      BOARD.y,
      BOARD.cellWidth,
      BOARD.height,
    );

    const targetLane = this.session.draft.target.lane;
    if (targetLane) {
      graphics.fillStyle(0xf2d685, 0.13);
      graphics.fillRect(
        BOARD.x + BOARD.cellWidth * (targetLane - 1),
        BOARD.y,
        BOARD.cellWidth,
        BOARD.height,
      );
      graphics.lineStyle(3, 0xf2d685, 0.8);
      graphics.strokeRect(
        BOARD.x + BOARD.cellWidth * (targetLane - 1) + 2,
        BOARD.y + 2,
        BOARD.cellWidth - 4,
        BOARD.height - 4,
      );
    }

    const source = this.session.state.board.find(
      (object) => object.id === this.session.draft.target.objectId,
    );
    const direction = this.session.draft.target.direction;
    const targetDepth = this.session.draft.target.depth;
    if (targetDepth) {
      if (source) {
        const point = this.positionToPoint({ lane: source.lane, depth: targetDepth });
        graphics.fillStyle(0xf2d685, 0.14);
        graphics.fillRect(
          point.x - BOARD.cellWidth / 2 + 3,
          point.y - BOARD.cellHeight / 2 + 3,
          BOARD.cellWidth - 6,
          BOARD.cellHeight - 6,
        );
        graphics.lineStyle(4, 0xf2d685, 0.85);
        graphics.strokeRect(
          point.x - BOARD.cellWidth / 2 + 3,
          point.y - BOARD.cellHeight / 2 + 3,
          BOARD.cellWidth - 6,
          BOARD.cellHeight - 6,
        );
      } else {
        const row = 3 - targetDepth;
        graphics.fillStyle(0xf2d685, 0.12);
        graphics.fillRect(
          BOARD.x,
          BOARD.y + BOARD.cellHeight * row,
          BOARD.width,
          BOARD.cellHeight,
        );
        graphics.lineStyle(3, 0xf2d685, 0.78);
        graphics.strokeRect(
          BOARD.x + 2,
          BOARD.y + BOARD.cellHeight * row + 2,
          BOARD.width - 4,
          BOARD.cellHeight - 4,
        );
      }
    }
    if (source && direction) {
      const destinationLane = source.lane + direction;
      if (destinationLane >= 1 && destinationLane <= 3) {
        const point = this.positionToPoint({
          lane: destinationLane as Lane,
          depth: source.depth,
        });
        graphics.lineStyle(4, 0xf2d685, 0.85);
        graphics.strokeRect(
          point.x - BOARD.cellWidth / 2 + 3,
          point.y - BOARD.cellHeight / 2 + 3,
          BOARD.cellWidth - 6,
          BOARD.cellHeight - 6,
        );
      }
    }
  }

  private clearSelectionGraphics(): void {
    this.selectionGraphics?.clear();
  }

  private updateLaneHeaders(board: readonly DreamObjectState[]): void {
    for (const lane of LANES) {
      const header = this.laneHeaders.get(lane);
      if (!header) continue;
      const impact = board
        .filter((object) => object.lane === lane)
        .reduce((sum, object) => sum + objectValue(object), 0);
      const selected = this.session.phase === "planning" && this.session.draft.focusLane === lane;
      header
        .setText("レーン " + lane + "   " + (impact > 0 ? "+" : "") + impact)
        .setColor(selected ? "#f2d685" : "#b9a978")
        .setScale(selected ? 1.06 : 1);
    }
  }

  private updatePreviousFocusMarkers(): void {
    const markerLayer = this.previousFocusMarkers;
    if (!markerLayer) return;
    markerLayer.removeAll(true);

    const plans = this.session.state.lastResolution?.plans;
    if (!plans) return;

    for (const lane of LANES) {
      const viewers = ([0, 1] as const).filter(
        (playerIndex) => plans[playerIndex].focusLane === lane,
      );
      viewers.forEach((playerIndex, index) => {
        const offset = viewers.length === 1 ? 0 : index === 0 ? -50 : 50;
        const marker = this.createPreviousFocusMarker(playerIndex)
          .setPosition(
            BOARD.x + BOARD.cellWidth * (lane - 0.5) + offset,
            BOARD.y - 53,
          );
        markerLayer.add(marker);
      });
    }
  }

  private createPreviousFocusMarker(playerIndex: PlayerIndex): Phaser.GameObjects.Container {
    const own = playerIndex === this.session.viewerIndex;
    const color = own ? 0x79ad82 : 0xc27b91;
    const backgroundColor = own ? 0x193a2d : 0x452733;
    const label = own ? "前回・自分" : "前回・相手";
    const background = this.add.graphics();
    background.fillStyle(backgroundColor, 0.94);
    background.fillRoundedRect(-47, -11, 94, 22, 11);
    background.lineStyle(1.5, color, 0.95);
    background.strokeRoundedRect(-47, -11, 94, 22, 11);
    background.fillStyle(color, 0.95);
    background.fillTriangle(-4, 11, 4, 11, 0, 17);

    const eye = this.add.graphics();
    eye.lineStyle(1.5, color, 1);
    eye.strokeEllipse(-32, 0, 20, 10);
    eye.fillStyle(color, 1);
    eye.fillCircle(-32, 0, 2.7);

    const text = this.add.text(-18, 0, label, {
      color: own ? "#d8ecd5" : "#f1d2dc",
      fontFamily: "sans-serif",
      fontSize: "9px",
      fontStyle: "bold",
    }).setOrigin(0, 0.5);

    return this.add.container(0, 0, [background, eye, text]);
  }

  private isObjectSelected(objectId: string): boolean {
    return this.session.phase === "planning"
      && this.session.draft.target.objectId === objectId;
  }

  private showMergedResult(object: DreamObjectState, resolve: () => void): void {
    const resultView = this.createObjectView(object, 0.55, 0);
    this.tweens.add({
      targets: resultView.container,
      alpha: 1,
      scaleX: 1,
      scaleY: 1,
      duration: 520,
      ease: "Back.Out",
      onComplete: resolve,
    });
  }

  private removeObjectView(id: string): void {
    const view = this.objectViews.get(id);
    if (!view) return;
    view.container.destroy(true);
    this.objectViews.delete(id);
  }

  private emitUiState(): void {
    const uiState: BattleUiState = this.session.getUiState();
    this.game.events.emit(GAME_EVENTS.uiState, uiState);
  }

  private emitHappiness(playerIndex: 0 | 1, value: number): void {
    this.game.events.emit(GAME_EVENTS.happiness, {
      playerIndex: this.displayPlayerIndex(playerIndex),
      value,
    });
  }

  private displayPlayerIndex(playerIndex: PlayerIndex): PlayerIndex {
    return playerIndex === this.session.viewerIndex ? 0 : 1;
  }

  private positionToPoint(position: BoardPosition): Phaser.Math.Vector2 {
    const row = 3 - position.depth;
    return new Phaser.Math.Vector2(
      BOARD.x + BOARD.cellWidth * (position.lane - 0.5),
      BOARD.y + BOARD.cellHeight * (row + 0.5),
    );
  }
}
